import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  MyAccountActivityLogDto,
  MyAccountProfileDto,
  MyAccountSaveResultDto,
  MyAccountUpdateDto,
  MyAccountViewDto,
} from './my-account.models';

interface CipaAccountProfileDto {
  id: number;
  name: string;
  birthDate: string;
  city: string;
  state: string;
  email: string;
  department: string;
  role: string;
  employeeCode: string;
  lastPasswordChangeAt: string | null;
  hasPasswordConfigured: boolean;
}

interface CipaAccountActivityLogDto {
  id: number;
  operation: string;
  actorName: string;
  occurredAt: string;
}

interface CipaAccountViewDto {
  profile: CipaAccountProfileDto;
  activityLogs: CipaAccountActivityLogDto[];
}

interface CipaAccountSaveResultDto {
  message: string;
  profile: CipaAccountProfileDto;
  activityLogs: CipaAccountActivityLogDto[];
}

@Injectable({
  providedIn: 'root',
})
export class MyAccountService {
  private readonly baseUrl = `${environment.smocipaApiBaseUrl}/cipa/account`;

  constructor(private readonly http: HttpClient) { }

  getMyAccountProfile(): Observable<MyAccountViewDto> {
    return this.http.get<CipaAccountViewDto>(this.baseUrl).pipe(
      map((response) => this.mapToViewDto(response)),
    );
  }

  updateMyAccount(input: MyAccountUpdateDto): Observable<MyAccountSaveResultDto> {
    return this.http.put<CipaAccountSaveResultDto>(this.baseUrl, input).pipe(
      map((response) => this.mapToSaveResultDto(response)),
    );
  }

  private mapProfile(profile: CipaAccountProfileDto): MyAccountProfileDto {
    return {
      id: profile.id,
      name: profile.name,
      birthDate: profile.birthDate,
      city: profile.city,
      state: profile.state,
      email: profile.email,
      department: profile.department,
      role: profile.role,
      employeeCode: profile.employeeCode,
      lastPasswordChangeAt: profile.lastPasswordChangeAt ?? '',
      hasPasswordConfigured: profile.hasPasswordConfigured,
    };
  }

  private mapActivityLog(log: CipaAccountActivityLogDto): MyAccountActivityLogDto {
    return {
      id: log.id,
      operation: log.operation,
      actorName: log.actorName,
      occurredAt: log.occurredAt,
    };
  }

  private mapToViewDto(response: CipaAccountViewDto): MyAccountViewDto {
    return {
      profile: this.mapProfile(response.profile),
      activityLogs: response.activityLogs.map((l) => this.mapActivityLog(l)),
    };
  }

  private mapToSaveResultDto(response: CipaAccountSaveResultDto): MyAccountSaveResultDto {
    return {
      message: response.message,
      profile: this.mapProfile(response.profile),
      activityLogs: response.activityLogs.map((l) => this.mapActivityLog(l)),
    };
  }
}
